<template>
  <v-row no-gutters>
    <v-col cols="6" v-if="isFetched">
      <v-row no-gutters class="pt-10">
        <v-col cols="auto">
          <base-button
            translation-path="admin.generateNewReport"
            :disabled="isReportGenerating"
            @click="generateNewReport"
          ></base-button>
        </v-col>
      </v-row>
      <v-row no-gutters class="pt-8">
        <v-col cols="11" style="border: 1px solid rgba(0, 0, 0, 0.12); max-width: 90vw">
          <v-data-table
            dense
            :items="computedReports"
            :headers="headers"
            calculate-widths
            :footer-props="{
              itemsPerPageOptions: [10, 20, 50, 100],
            }"
          >
            <template #item.status="{item}">
              {{ $i18n.t(`default.reportsStatuses.${item.status}`) }}
            </template>
            <template #item.creation_end="{item}">
              {{ item.creation_end ? $dayjs(item.creation_end).format('L') : $i18n.t('admin.noData') }}
            </template>
            <template #item.download="{item}">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  <span v-on="on">
                    <v-icon @click="downloadReport(item.tid)" :disabled="item.isGenerating" color="primary">
                      mdi-download
                    </v-icon>
                  </span>
                </template>
                {{ item.tooltip }}
              </v-tooltip>
            </template>
            <template #item.actions="{item}">
              <dots-menu
                :items="dotsItems[item.action]"
                @delete="openDeleteDialog(item)"
                @cancel="openCancelDialog(item)"
              ></dots-menu>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <main-dialog
        v-if="isDeleteDialogLoaded"
        :is-visible.sync="isDeleteDialogVisible"
        :title="$i18n.t('dialog.title.deleteReport')"
        :body="$i18n.t('dialog.body.deleteReportConfirmation')"
        @agree="handleDeleting"
        :loading="isConfirmButtonLoading"
        agree-text="dialog.agreeDelete"
      />
      <main-dialog
        v-if="isCancelDialogLoaded"
        :is-visible.sync="isCancelDialogVisible"
        :title="$i18n.t('dialog.title.cancelReport')"
        :body="$i18n.t('dialog.body.cancelReportConfirmation')"
        @agree="handleCanceling"
        :loading="isConfirmButtonLoading"
        agree-text="dialog.agreeYes"
      />
    </v-col>
    <v-col v-else>
      <main-loader></main-loader>
    </v-col>
  </v-row>
</template>

<script>
import { call } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';

const apiUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'ZdmReportsTab',
  components: {
    MainLoader,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  data: vm => ({
    isFetched: false,
    search: '',
    dotsItems: {
      cancel: [
        {
          name: 'cancel',
        },
      ],
      delete: [
        {
          name: 'delete',
        },
      ],
    },
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    deletingReport: undefined,
    isCancelDialogVisible: false,
    isCancelDialogLoaded: false,
    cancelingReport: undefined,
    isConfirmButtonLoading: false,
    headers: [
      {
        text: vm.$i18n.t('admin.generateDate'),
        value: 'creation_end',
        align: 'center',
      },
      {
        text: vm.$i18n.t('admin.status'),
        value: 'status',
        align: 'center',
      },
      {
        text: vm.$i18n.t('admin.download'),
        value: 'download',
        sortable: false,
        align: 'center',
        width: '50px',
      },
      {
        text: '',
        value: 'actions',
        align: 'center',
        sortable: false,
        width: '50px',
      },
    ],
    reports: [],
  }),
  computed: {
    isReportGenerating() {
      return this.reports.some(report => report.status === 'PENDING');
    },
    computedReports() {
      return this.reports.map(report => {
        const isGenerating = report.status === 'PENDING';
        return {
          ...report,
          isGenerating,
          tooltip: this.$i18n.t(isGenerating ? 'admin.duringGenerationDownload' : 'admin.downloadReport'),
        };
      });
    },
  },
  methods: {
    getReports: call('zdm/getReports'),
    cancelReport: call('celeryTasks/stopCeleryTask'),
    deleteReport: call('zdm/deleteReport'),
    generateNewReportAction: call('zdm/generateNewReport'),
    downloadReport(id) {
      window.open(`${apiUrl}/celery/get/${id}`);
    },
    async generateNewReport() {
      try {
        await this.generateNewReportAction();
        await this.init();
      } catch (error) {
        console.log('  error', error);
      }
    },
    openCancelDialog(report) {
      this.cancelingReport = report;
      this.isCancelDialogVisible = true;
      this.isCancelDialogLoaded = true;
    },
    openDeleteDialog(report) {
      this.deletingReport = report;
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;
    },
    async handleCanceling() {
      const { tid } = this.cancelingReport;
      try {
        this.isConfirmButtonLoading = true;
        await this.cancelReport(tid);
        await this.init();
        this.isConfirmButtonLoading = false;
        this.isCancelDialogVisible = false;
      } catch (error) {
        console.log(error);
        this.isConfirmButtonLoading = false;
        this.isCancelDialogVisible = false;
      }
    },
    async handleDeleting() {
      const { tid } = this.deletingReport;
      const index = this.getReportIndex(tid);
      try {
        this.isConfirmButtonLoading = true;
        this.deleteReportLocally(index);
        await this.deleteReport(tid);
        this.isDeleteDialogVisible = false;
        this.isConfirmButtonLoading = false;
      } catch (error) {
        console.log(error);
        this.addReportAtIndex(this.deletingReport, index);
        this.isConfirmButtonLoading = false;
        this.isDeleteDialogVisible = false;
      }
    },
    getReportIndex(id) {
      return this.reports.map(report => report.tid).indexOf(id);
    },
    deleteReportLocally(index) {
      this.$delete(this.reports, index);
    },
    addReportAtIndex(report, index) {
      this.reports.splice(index, 0, report);
    },
    async init() {
      try {
        const r = await this.getReports();
        this.reports = r.data.data;
        this.isFetched = true;
      } catch (error) {
        console.log('  error', error);
      }
    },
  },
  created() {
    this.init();
  },
};
</script>

<style scoped lang="scss"></style>
